import * as React from "react";
import { SearchInput } from "../../Shared/SearchInput/SearchInput";
import { Navigator } from "../../Shared/Wizards/Entities/Navigator";
import { Urls } from "../Urls";
import { groupBy } from "lodash";
import { SearchActorNameClient } from "../../../Wrap/Api/Customer/SearchActorName";
import { SearchActorNameModel } from "../../../Wrap/Models/Domains/Account/SearchActorNameModel";
import { SearchActorNameType } from "../../../Wrap/Constants/Enums/SearchActorNameType";
import { accountTypeMap, ClientRecordTab, roleTypeMap } from "./Clients/ClientRecord/Resource/Contents";
import { HierarchyLevelName } from "../../../Wrap/Constants/Enums/HierarchyLevelName";
import { AccountType } from "../../../Wrap/Constants/Enums/Domain/AccountType";
import { HierarchyIdHelper } from "../../Shared/Common/HierarchyIdHelper";
import { StringHelper } from "../../../react-core/Helpers/StringHelper";
import CoachMarker from "../../Shared/CoachMarkers/CoachMarker";
import { checkPermission } from "../../Shared/Permission/PermissionHelper";
import { UserClaim } from "../../../Wrap/Constants/Enums/UserClaim";
import { ClientRecordAction } from "./Clients/ClientRecord/Action/ClientRecordAction";
import { ClientRecordStore } from "./Clients/ClientRecord/Store/Variable";
import { FormComponent } from "../../../react-core/Html/FormComponent";
import { PersonalDetail } from "../../../Wrap/Models/Models/Customer/PersonalDetail";
import { CustomerTaxResidencyModel } from "../../../Wrap/Models/Models/Customer/Regulatory/CustomerTaxResidencyModel";
import { AccountRoleByActorDetails } from "../../../Wrap/Models/Domains/Account/AccountRoleByActorDetails";
import { CustomerEmailAddress } from "../../../Wrap/Models/Models/Customer/CustomerEmailAddress";
import { JsonDateParser } from "../../../react-core/Utils/JsonDateParser";
import { EmailAddressType } from "../../../Wrap/Constants/Enums/EmailAddressType";
import { TaxReferenceType } from "../../../Wrap/Constants/Enums/Customer/TaxReferenceType";
import { ActorOwner } from "../../../Wrap/Models/Domains/Customers/ActorOwner";
import AriaTooltip from "../../Shared/Tooltip/AriaTooltip";

interface ISecuredSearchProps extends React.Props<SecuredSearch> { }

interface ISecuredSearchState {
    input: string;
    personalDetail: PersonalDetail;
    accountRoleDetails: Array<AccountRoleByActorDetails>;
    emailDetails: CustomerEmailAddress[];
    taxResidency: Array<CustomerTaxResidencyModel>;
    searchActorNameType: SearchActorNameType;
    accountHierarchyId: string;
    actorOwner: ActorOwner;
    actorName: string;
}

export class SecuredSearch extends FormComponent<ISecuredSearchProps, ISecuredSearchState> {
    constructor(props: any) {
        super(props);
        this.state = {
            input: null,
            personalDetail: null,
            accountRoleDetails: null,
            emailDetails: null,
            taxResidency: null,
            searchActorNameType: null,
            accountHierarchyId: null,
            actorOwner: null,
            actorName: null
        };

        this.subscribeToStore(ClientRecordStore, () => this.onClientRecordChange());
    }

    private onClientRecordChange() {
        const data = ClientRecordStore.getData();

        if (data[ClientRecordTab.PersonalDetail]) {
            this.setState({ personalDetail: data[ClientRecordTab.PersonalDetail] });
        }

        if (data.EmailDetail) {
            this.setState({ emailDetails: data.EmailDetail });
        }
    }

    private search(v: string) {
        this.setState({
            personalDetail: null,
            accountRoleDetails: null,
            emailDetails: null,
            taxResidency: null,
            searchActorNameType: null,
            accountHierarchyId: null,
            actorOwner: null,
            actorName: null
        })
        const regex = /^(SP|WP)\d{1,3}$/;
        if(!regex.test(v.toUpperCase())){
            const client = new SearchActorNameClient();
            return client.get(v.toUpperCase());
        }
    }

    private getActorDetails(actorId: number) {
        const {
            searchActorNameType
        } = this.state;
        if (actorId == 0) {
            return
        }

        ClientRecordAction.getAccountRoleDetail(
            actorId.toString(),
            (results: Array<AccountRoleByActorDetails>) => {
                this.setState({ accountRoleDetails: results });
            }
        );
        if (searchActorNameType == SearchActorNameType.Customer) {
            ClientRecordAction.getPersonalDetail(actorId.toString());
            ClientRecordAction.getEmailDetail(actorId.toString());
            ClientRecordAction.getCutomerTaxResidency(Number(actorId), (val) => {
                this.setState({
                    taxResidency: val
                });
            });
        }

        ClientRecordAction.getActorOwner(
            actorId,
            (results: ActorOwner) => {
                this.setState({ actorOwner: results });
            }
        );
    }

    private renderResultTable(data: SearchActorNameModel[], inputString: string) {
        // Group data by different dimension - customer, account, orgnasation
        const customers = data?.filter((ele) => ele.SearchActorNameType == SearchActorNameType.Customer) || [];
        const accounts = data?.filter((ele) => ele.SearchActorNameType == SearchActorNameType.Account) || [];
        const organisations = data?.filter((ele) => ele.SearchActorNameType == SearchActorNameType.Organisation) || [];

        const {
            searchActorNameType
        } = this.state;

        if (!searchActorNameType) {
            if (customers.length > 0) {
                const firstCustomer = customers[0];
                this.setState({ searchActorNameType: SearchActorNameType.Customer, actorName: firstCustomer.CustomerName }, () => {
                    this.getActorDetails(firstCustomer.CustomerId)
                })
            } else if (accounts.length > 0) {
                const firstAccount = accounts[0];
                if (firstAccount.AccountType !== AccountType.Consolidated) {
                    this.setState({ searchActorNameType: SearchActorNameType.Account, accountHierarchyId: null, actorName: firstAccount.CustomerName }, () => {
                        this.getActorDetails(firstAccount.CustomerId)
                    })
                } else {
                    this.setState({ searchActorNameType: SearchActorNameType.Account, accountHierarchyId: firstAccount.AccountHierarchyId })
                }
            } else if (organisations.length > 0) {
                const firstOrganisation = organisations[0];
                this.setState({ searchActorNameType: SearchActorNameType.Organisation, actorName: firstOrganisation.OrganisationName }, () => {
                    this.getActorDetails(firstOrganisation.CustomerId)
                })
            }
        }

        return (
            <>
                <div className="quick-search-panel">
                    <div id="smart-search">
                        <div className="mb-12">
                            <b>{customers.length + accounts.length + organisations.length} results</b>
                        </div>
                        <div className="row narrow-gutter">
                            <div className="col-12 col-md-5">
                                <div className="scrollpane h60v">
                                    <div className="mb-12" key={"customer-search-result"} id={"customer-search-result"}>
                                        <b>Clients</b>
                                    </div>
                                    <ul className="list-view">
                                        {customers.length > 0 ? (

                                            customers
                                                .slice(0, 10)
                                                .map((data: SearchActorNameModel, index: number) =>
                                                    this.renderCustomerList(data, index, inputString)
                                                )
                                        ) : (
                                            <li className="" key={`customer-search-no-result`} id={`customer-search-result-${0}`}>
                                                <div id={`ui-id-${0}`} className="" tabIndex={0}>
                                                    <span>0 results returned.</span>
                                                </div>
                                            </li>
                                        )}
                                    </ul>
                                    {customers.length > 10 && (
                                        <div
                                            className="mb-12 mt-12"
                                            key={`customer-search-too-many-result`}
                                            id={`customer-search-result-${11}`}
                                        >
                                            <div id={`ui-id-${0}`} className="text-size-14" tabIndex={0}>
                                                <span>
                                                    Your search returned more than 10 records. Try a more precise search in order to refine
                                                    results.
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    <div className="mb-12 mt-12" key={"account-search-result"} id={"account-search-result"}>
                                        <b>Accounts</b>
                                    </div>

                                    <ul className="list-view">
                                        {accounts.length > 0 ? (
                                            accounts
                                                .slice(0, 10)
                                                .map((data: SearchActorNameModel, index: number) =>
                                                    this.renderAccountList(data, index, inputString)
                                                )
                                        ) : (
                                            <div className="mb-12" key={`account-search-no-result`} id={`account-search-result-${0}`}>
                                                <div id={`ui-id-${0}`} className="ui-menu-item-wrapper" tabIndex={0}>
                                                    <span>0 results returned.</span>
                                                </div>
                                            </div>
                                        )}
                                    </ul>
                                    {accounts.length > 10 && (
                                        <div
                                            className="mb-12 mt-12"
                                            key={`account-search-too-many-result`}
                                            id={`account-search-result-${11}`}
                                        >
                                            <div id={`ui-id-${0}`} className="text-size-14" tabIndex={0}>
                                                <span>
                                                    Your search returned more than 10 records. Try a more precise search in order to refine
                                                    results
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                    <div className="mb-12 mt-12" key={"organisation-search-result"} id={"organisation-search-result"}>
                                        <b>Organisations</b>
                                    </div>
                                    <ul className="list-view">
                                        {organisations.length > 0 ? (
                                            organisations
                                                .slice(0, 10)
                                                .map((data: SearchActorNameModel, index: number) =>
                                                    this.renderOrganisationList(data, index, inputString)
                                                )
                                        ) : (
                                            <div
                                                className="mb-12"
                                                key={`organisation-search-no-result`}
                                                id={`organisation-search-result-${0}`}
                                            >
                                                <div id={`ui-id-${0}`} className="ui-menu-item-wrapper" tabIndex={0}>
                                                    <span>0 results returned.</span>
                                                </div>
                                            </div>
                                        )}
                                    </ul>
                                    {organisations.length > 10 && (
                                        <div
                                            className="mb-12 mt-12"
                                            key={`organisation-search-too-many-result`}
                                            id={`organisation-search-result-${11}`}
                                        >
                                            <div id={`ui-id-${0}`} className="text-size-12" tabIndex={0}>
                                                <span>
                                                    Your search returned more than 10 records. Try a more precise search in order to refine
                                                    results
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="col-12 col-md-7">
                                {this.renderActorDetails()}
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    private renderRoles(rData: Array<AccountRoleByActorDetails>) {
        if (!rData || rData?.length == 0) {
            return "None"
        }

        const result = [];

        const groupbyData = groupBy(rData, "ClAccountId")
        Object.keys(groupbyData).forEach((ele) => {
            result.push(
                <div className="text-size-14 mb-4">
                    {ele} <br></br> ({groupbyData[ele].map(role => roleTypeMap[role.AccountRoleType]).join(', ')})
                </div>
            );
        });

        return result;
    };

    private renderActorDetails() {
        const {
            personalDetail,
            accountRoleDetails,
            emailDetails,
            taxResidency,
            searchActorNameType,
            accountHierarchyId,
            actorName,
            actorOwner
        } = this.state;
        if (!searchActorNameType) {
            return;
        } else if (searchActorNameType == SearchActorNameType.Account && accountHierarchyId) {
            return (
                <>
                <div className="panel details-panel mb-0">
                    <div className="p-section">
                        <button
                            className="btn primary mb-0"
                            onClick={() => {
                                checkPermission(UserClaim.AdviserPortalClientSummaryCanView)
                                    ? window.location.assign(
                                        `/wpadviser/en-gb/dashboard/home/main/customer/${accountHierarchyId}/summary`
                                    )
                                    : null;
                            }}
                        >
                            View client
                        </button>
                    </div>
                    </div>
                </>
            )
        }

        return (
            <>
                <div className="panel details-panel mb-0">
                    <div className="p-section">
                        <h2>{actorName}</h2>
                        <div>Adviser: {actorOwner?.AdviserName}</div>
                    </div>
                    <div className="p-section">
                        <dl className="def-list in-search-panel mb-0">
                            <dt>Account number</dt><dd>{this.renderRoles(accountRoleDetails)}</dd>
                        </dl>

                        {searchActorNameType == SearchActorNameType.Customer && (
                            <>
                                <dl className="def-list in-search-panel mb-0">

                                    <dt>Date of birth</dt>
                                    <dd>{personalDetail && personalDetail?.DateOfBirth && JsonDateParser.getFormatDate(personalDetail?.DateOfBirth, "dd/MM/yyyy")}
                                        <span className="ml-3">(age {personalDetail && personalDetail?.DateOfBirth && JsonDateParser.calculateAge(personalDetail?.DateOfBirth).toString()})</span>
                                    </dd>

                                    <dt>Email</dt>
                                    <dd className="break-word">{emailDetails?.find(ele => ele.EmailAddressType == EmailAddressType.Primary)?.EmailAddress}</dd>

                                    <dt>NINO / Identifier</dt>
                                    <dd>{taxResidency?.find(ele => ele.TaxReferenceType == TaxReferenceType.GbrNino)?.TaxReference}</dd>

                                </dl>
                            </>
                        )}
                    </div>
                    <div className="p-section">
                        <div className="row">
                            <div className="col-fluid col-right">
                                <button
                                    className="btn primary mb-0"
                                    onClick={() => {
                                        checkPermission(UserClaim.AdviserPortalClientSummaryCanView)
                                            ? window.location.assign(
                                                `/wpadviser/en-gb/dashboard/home/main/customer/${HierarchyIdHelper.toHierarchyIdString(
                                                    HierarchyLevelName.Customer,
                                                    actorOwner?.ActorId
                                                )}/summary`
                                            )
                                            : null;
                                    }}
                                >
                                    View client
                                </button>
                            </div>
                        </div>
                    </div>

                </div>
            </>)
    }

    private renderCustomerList(data: SearchActorNameModel, index: number, inputString: string): JSX.Element {
        const {
            searchActorNameType,
            actorOwner
        } = this.state;
        return (
            <li
                className={`lv-item ${searchActorNameType == SearchActorNameType.Customer && data?.CustomerId == actorOwner?.ActorId ? "is-selected" : ""}`}
                key={`customer-search-result-${index}`}
                id={`customer-search-result-${index}`}
            >
                <div id={`customer-search-result-ui-id-${index}`} className="ui-menu-item-wrapper">
                    <div className="row">
                        <div className="col-fluid">
                            <button
                                className="lv-single-action"
                                onClick={() => {
                                    this.getActorDetails(data?.CustomerId)
                                    this.setState({ searchActorNameType: SearchActorNameType.Customer, actorName: data?.CustomerName })
                                }}
                            >
                                <div>
                                    <span>{StringHelper.getHighlightedText(`${data?.CustomerName}`, inputString.split(" "))}</span>
                                </div>
                                <div className="text-size-12 mb-8">
                                    <span>ID:</span>
                                    <span>{data?.CustomerId}</span>
                                </div>
                                <hr className="mb-8 mt-8"></hr>
                                <div className="text-size-12">Type: Client</div>
                            </button>
                        </div>
                    </div>
                </div>
            </li>
        );
    }


    private renderAccountList(data: SearchActorNameModel, index: number, inputString: string): JSX.Element {
        const {
            searchActorNameType,
            accountHierarchyId,
            actorOwner
        } = this.state;
        return (
            <li
                className={`lv-item ${searchActorNameType == SearchActorNameType.Account && (data?.CustomerId == actorOwner?.ActorId || data?.AccountHierarchyId == accountHierarchyId) ? "is-selected" : ""}`}
                key={`account-search-result-${index}`}
                id={`account-search-result-${index}`}
            >
                <div id={`account-search-result-ui-id-${index}`} className="ui-menu-item-wrapper">
                    <button
                        className="lv-single-action"
                        onClick={() => {
                            if (data?.AccountType !== AccountType.Consolidated) {
                                this.getActorDetails(data?.CustomerId)
                                this.setState({ searchActorNameType: SearchActorNameType.Account, accountHierarchyId: null, actorName: data?.CustomerName })
                            } else {
                                this.setState({ searchActorNameType: SearchActorNameType.Account, accountHierarchyId: data?.AccountHierarchyId })
                            }
                        }}
                    >
                        {data?.AccountType == AccountType.Consolidated
                            ? (
                                <>
                                    <div className="mb-8">
                                        {StringHelper.getHighlightedText(`${data?.ClAccountId}, ${accountTypeMap[data?.AccountType]}`, inputString.split(" "))}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div>
                                        {StringHelper.getHighlightedText(`${data?.CustomerName}, ${data?.ClAccountId}, ${accountTypeMap[data?.AccountType]}`, inputString.split(" "))}
                                    </div>
                                    <div className="text-size-12 mb-8">
                                        <span>Client ID:</span>
                                        <span>{data?.CustomerId}</span>
                                    </div>
                                </>
                            )}
                        <hr className="mb-8 mt-8"></hr>
                        <div className="text-size-12">Type: Account</div>
                    </button>
                </div>
            </li>
        );
    }

    private renderOrganisationList(data: SearchActorNameModel, index: number, inputString: string): JSX.Element {
        const {
            searchActorNameType,
            actorOwner
        } = this.state;
        return (
            <li
                className={`lv-item ${searchActorNameType == SearchActorNameType.Organisation && data?.CustomerId == actorOwner?.ActorId ? "is-selected" : ""}`}
                key={`organisation-search-result-${index}`}
                id={`organisation-search-result-${index}`}
            >
                <div id={`organisation-search-result-ui-id-${index}`} className="ui-menu-item-wrapper">
                    <button
                        onClick={() => {
                            this.getActorDetails(data?.CustomerId)
                            this.setState({ searchActorNameType: SearchActorNameType.Organisation, actorName: data?.OrganisationName })
                        }}
                        className="lv-single-action"
                    >
                        <div>
                            <span>{StringHelper.getHighlightedText(`${data?.OrganisationName}`, inputString.split(" "))}</span>
                        </div>
                        <div className="text-size-12 mb-8">
                            <span>ID:</span>
                            <span>{data?.CustomerId}</span>
                        </div>
                        <hr className="mb-8 mt-8"></hr>
                        <div className="text-size-12">Type: Organisation</div>
                    </button>
                </div>
            </li>
        );
    }

    public render(): JSX.Element {
        return (
            <div className="ist-wrap is-secured">
                <CoachMarker coachMarkerKey="secured-search" />
                <div className="advanced-search">
                    <SearchInput
                        cssWrapper="label-sr-only submit-is-magnifier-glass mb-2"
                        apiClient={(v) => {
                            return this.search(v);
                        }}
                        minLength={3}
                        customeChildren={(data, inputString) => this.renderResultTable(data, inputString)}
                        placeholder={"Search by name or account number"}
                        label="Search bar"
                        triggerOnlyByEnterKey={true}
                    />
                    <button
                        className="ist-advanced-toggle"
                        aria-expanded={false}
                        style={{ zIndex: 999 }} >
                        <AriaTooltip cssWrapper="is-info-icon" showCloseButton={true}>
                            <p className="mb-8">Quick search allows you to search by name or account number. </p>
                            <p className="mb-8">From the search results you can click through to view key client data helping you easily identify the client prior to accessing the account.</p>
                            <p className="mb-0">Key data includes adviser name, account numbers, client role, date of birth/age, email address and NINO/identifier.</p>
                        </AriaTooltip>
                    </button>
                </div>
            </div>
        );
    }
}
